import { Loading } from '@rd-web-markets/shared/dist/util';
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom';
import { Alert, Card } from 'react-bootstrap';
import { handleError } from '@rd-web-markets/shared/dist/store/features/alertSlice';
import { useDispatch, useSelector } from 'react-redux';
import claimGroupService from '@rd-web-markets/shared/dist/services/claim_group.service';
import ClaimGroupBreadcrumbLinks from '@rd-web-markets/shared/dist/claim_group/edit/ClaimGroupBreadcrumbLinks';
import ClaimGroupEditNavigation from '@rd-web-markets/market/dist/claim_group/edit/ClaimGroupEditNavigation';
import ClaimGroupEditFooter from '@rd-web-markets/shared/dist/claim_group/edit/ClaimGroupEditFooter';
import { claimTemplateService } from '@services/claim_template.service';
import questionnairesService from '@services/questionnaires.service';
import CompanyClaimGroupEditForm from '@rd-web-markets/shared/dist/company/CompanyClaimGroupEditForm';
import { SPRINT_CLAIM_NAME, SHOW_NOTIFY_CLAIM_USERS_MODAL } from '@rd-web-markets/market/dist/constants';
import { fetchAndSetAdmins, fetchAndSetConsultants } from 'src/effects/user.effects';
import ClaimGroupSidebar from '@rd-web-markets/shared/dist/pages/setup/ClaimGroupSidebar';
import { useGetUserClaimGroupAbilities } from '@rd-web-markets/market/dist/userAbilities';
import { userListService } from '@services/user_list_service';
import customMenuCreatorService from '@rd-web-markets/shared/dist/services/company/custom_menu_creator.service';
import { claimGroupMenuItemsCreator } from '@rd-web-markets/market/dist/company/claimGroupMenuItemsCreator';
import GeneralBreadcrumb from '@rd-web-markets/shared/dist/util/breadcrumbs/GeneralBreadcrumb';
import UnsavedChangesPrompt from '@rd-web-markets/shared/dist/components/UnsavedChangesPrompt';
import NotifyClaimUsersModal from '@rd-web-markets/shared/dist/company/NotifyClaimUsersModal';
import { useSetCompanyBreadcrumb } from '@rd-web-markets/shared/dist/util/hooks/useSetCompanyBreadcrumb';

const ClaimGroupEditGeneralPage = ({handleToaster, accountType}) => {
  const { claimGroupId } = useParams();
  const [claimGroup, setClaimGroup] = useState(null);
  const [claimTemplates, setClaimTemplates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tasks, setTasks] = useState(null);
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const dispatch = useDispatch();
  const [consultants, setConsultants] = useState(null);
  const [admins, setAdmins] = useState(null);
  const [managers, setManagers] = userListService.useFetchUsersByType({ userType: 'manager', setLoading })
  const [isUnderReview, setisUnderReview] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const userClaimGroupAbilities = useGetUserClaimGroupAbilities({ user, claimGroup })
  const pageAccess = userClaimGroupAbilities.pages.ClaimGroupEditGeneralPage
  const [customMenuItems] = customMenuCreatorService.usePrepare({id: claimGroupId, step: 'bfsz'}, {claim_group_id: claimGroupId}, null, claimGroupMenuItemsCreator)

  useSetCompanyBreadcrumb(claimGroup?.company, accountType);

  // Track whether tasks have been modified by the user
  const [tasksModified, setTasksModified] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const originalAdminId = useRef(null);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  const [questionnaires] = questionnairesService.useQuestionnaires();

  const transformClaimGroup = claimGroup => {
    setClaimGroup({
      ...claimGroup,
      consultants_attributes: claimGroup.consultant_roles
    });
  };

  useEffect(() => {
    fetchAndSetConsultants(setConsultants,dispatch, handleError);
    fetchAndSetAdmins(setAdmins, dispatch, handleError);
    const getData = async () => {
      try {
        const claimGroup = await claimGroupService.get(claimGroupId);
        if (process.env.PUBLIC_URL !== '/be') {
          const claimTemplates = await claimTemplateService.get();
          setClaimTemplates(claimTemplates);
          const tasks = [];
          const defaultTemplate = claimTemplates.find(template => template.name === SPRINT_CLAIM_NAME);

          if(defaultTemplate) {
            defaultTemplate.claim_template_categories.forEach(category => {
              category.claim_template_tasks.forEach(task => tasks.push({...task, category_title: category.title, included: true }));
            });
          }
          setDefaultTemplate(defaultTemplate);
          setTasks(tasks);
          
          setTasksModified(false);
        }

        transformClaimGroup(claimGroup);
        originalAdminId.current = claimGroup.admin_id;
        if (claimGroup?.active_change_set) {
          setisUnderReview(true);
        }
      } catch (error) {
        dispatch(handleError(error));
      }
    };
    getData();
  }, [claimGroupId, dispatch]);

  const hasManagerChanged = () => {
    return claimGroup.admin_id && 
           originalAdminId.current !== null && 
           String(claimGroup.admin_id) !== String(originalAdminId.current);
  };

  const getNewManager = () => {
    const allAdmins = [...(admins || []), ...(managers || [])];
    const newAdmin = allAdmins.find(a => String(a.id) === String(claimGroup.admin_id));
    if (newAdmin) {
      return [{ id: newAdmin.id, name: newAdmin.name, email: newAdmin.email }];
    }
    return [];
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (SHOW_NOTIFY_CLAIM_USERS_MODAL && hasManagerChanged()) {
      setPendingSubmitEvent(e);
      setShowNotifyModal(true);
      return;
    }

    await submitClaimGroup([]);
  };

  const submitClaimGroup = async (notifyUserIds) => {
    setLoading(true);
    
    if(tasks && tasksModified){
      claimGroup.tasksJSON = tasks.filter(task => task.included === true);
    }

    claimGroup.notify_user_ids = notifyUserIds;
    
    try {
      await claimGroupService.update(claimGroup);
      setTasksModified(false);
      setHasUnsavedChanges(false);
      originalAdminId.current = claimGroup.admin_id;
    } catch(error) {
      dispatch(handleError(error));
    }
    setLoading(false);
  };

  const addConsultantsToClaimGroupAndNotify = (selectedUserIds) => {
    setShowNotifyModal(false);
    setPendingSubmitEvent(null);
    submitClaimGroup(selectedUserIds);
  };

  const handleChange = (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setHasUnsavedChanges(true);
    setClaimGroup({
      ...claimGroup,
      [event.target.name]: value,
    });
  };

  const handleClaimTypeChange = e => {
    const template = claimTemplates.find(template => template.id === +e)
    const newTasks = [];

    template.claim_template_categories.forEach(category => {
      category.claim_template_tasks.forEach(task => newTasks.push({...task, category_title: category.title, included: true }));
    });
    setHasUnsavedChanges(true);
    setTasks([...newTasks]);
    setTasksModified(true);
  };

  const handleRadioButtonChange = event => {
    const value = event.target.value === 'true' ? true : false;
    setHasUnsavedChanges(true);
    setClaimGroup({
      ...claimGroup,
      [event.target.name]: value
    });
  };

  const updateTasks = (tasks) => {
    setHasUnsavedChanges(true);
    setTasks(tasks);
    setTasksModified(true);
  };

  if(!claimGroup || ((!claimTemplates || !tasks) && process.env.PUBLIC_URL !== '/be') || !consultants || !admins || !questionnaires) return <Loading />
  

  return (
    <>
      <UnsavedChangesPrompt hasUnsavedChanges={hasUnsavedChanges} />
      { isUnderReview && 
        <Alert key='warning' variant='warning'>
          Claim info can not be edited after manager or client review has started  
        </Alert> 
      }

      <ClaimGroupSidebar
        claimGroup={claimGroup}
        accountType={accountType}
        highlightedMenuItem='edit/general'
        company={claimGroup.company}
        claims={claimGroup.claims}
        customMenuItems={customMenuItems}
      />

      {process.env.PUBLIC_URL === '/de' ? (
        <GeneralBreadcrumb claimGroup={claimGroup} items={['home', 'companies', 'company', 'edit_claim_info']}/>
      ): (
        <ClaimGroupBreadcrumbLinks claimGroup={claimGroup}/>
      )}

      <div className="ps-4 pe-4">
        <h2 className='mt-2 mb-3 text-primary'>Claim Settings</h2>
        <Card>
          <Card.Body>
            <CompanyClaimGroupEditForm
              handleClaimGroupChange={handleChange}
              handleClaimTypeChange={handleClaimTypeChange}
              updateTasks={updateTasks}
              claimTemplates={claimTemplates}
              defaultTemplate={defaultTemplate}
              handleRadioButtonChange={handleRadioButtonChange}
              claimGroup={claimGroup}
              setClaimGroup={(newClaimGroup) => {
                setHasUnsavedChanges(true);
                setClaimGroup(newClaimGroup);
              }}
              tasks={tasks}
              admins={admins}
              managers={managers}
              consultantsAndAdmins={[...consultants, ...managers, ...admins]}
              onUpdateConsultants={transformClaimGroup}
              isUnderReview={isUnderReview}
              questionnaires={questionnaires}
              pageAccess={pageAccess}
              handleToaster={handleToaster}
            />
            <ClaimGroupEditFooter
              loading={loading}
              handleSubmit={handleSubmit}
              backUrl={`/${accountType}/claim_groups/${claimGroup.id}/project_overview`}
              continueUrl={`/${accountType}/claim_groups/${claimGroup.id}/project_overview`}
              isUnderReview={isUnderReview}
            />
          </Card.Body>
        </Card>
      </div>

      {showNotifyModal &&
        <NotifyClaimUsersModal
          show={showNotifyModal}
          onHide={() => {
            setShowNotifyModal(false);
            setPendingSubmitEvent(null);
          }}
          onSubmit={addConsultantsToClaimGroupAndNotify}
          users={getNewManager()}
          title="Notify Users of New Claim"
          mode="update"
        />
      }
    </>
  )
}

export default ClaimGroupEditGeneralPage
